import { FC, Fragment } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Grid,
  Stack,
  TextField,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useAuth } from '@/features/auth'
import { useLocalisation } from '@/utils/localisation'
import { error } from '@/utils/logger'

import { EmailFormSchema, EmailFormSchemaType } from './schema'
import { useEmailUpdate } from '../api/useEmailUpdate'

export const EmailSettings: FC = () => {
  const { translate } = useLocalisation()
  const { user } = useAuth()
  const toaster = useToaster()
  const emailMutation = useEmailUpdate()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormSchemaType>({
    resolver: zodResolver(EmailFormSchema),
  })

  const onSubmit: SubmitHandler<EmailFormSchemaType> = async (data) => {
    if (user) {
      await emailMutation.mutateAsync({
        customerId: user.customer_id ?? '?',
        userId: user._id ?? '?',
        data: {
          email: data.email,
        },
      })
      toaster({
        title: translate('settings.email.notifications.updated', {
          email: data.email,
        }),
      })
    } else {
      error('User tried to update but has no valid auth object')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle>{translate('settings.email.heading')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack gap={16}>
            <Typography variant="body">
              {translate('settings.email.intro', {
                email: user?.email,
              })
                .split(user?.email ?? '')
                .map((part, index) => (
                  <Fragment key={index}>
                    {index === 1 ? <Typography variant="body-bold">{user?.email}</Typography> : null}
                    {part}
                  </Fragment>
                ))}
            </Typography>
            <Grid col={2} gap={16}>
              <TextField
                type="email"
                label={translate('settings.email.fields.email.label')}
                placeholder={translate('settings.email.fields.email.placeholder')}
                required
                {...register('email')}
                state={errors.email ? 'negative' : undefined}
              />

              <TextField
                label={translate('settings.email.fields.confirm.label')}
                placeholder={translate('settings.email.fields.confirm.placeholder')}
                required
                {...register('confirmEmail')}
                state={errors.confirmEmail ? 'negative' : undefined}
              />
            </Grid>

            <Typography color="muted" variant="small">
              {translate('settings.email.outro')}
            </Typography>
          </Stack>
        </CardContent>
        <CardFooter>
          <Button type="submit">{translate('settings.email.actions.update')}</Button>
        </CardFooter>
      </Card>
    </form>
  )
}
